import IconChevronDown from '@apollo/icons/small/IconChevronDown.svg';
import {
  Button,
  Menu,
  MenuButton,
  MenuContent,
  MenuDivider,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
} from '@apollo/orbit';
import React from 'react';

import { assertUnreachable } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { LintDiagnosticLevel } from 'src/lib/graphqlTypes/types';

import { SeverityLevelIcon } from './SeverityLevelIcon';

const getSeverityLevelName = (level: GraphQLTypes.LintDiagnosticLevel) => {
  switch (level) {
    case LintDiagnosticLevel.ERROR:
      return 'Error';
    case LintDiagnosticLevel.WARNING:
      return 'Warn';
    case LintDiagnosticLevel.IGNORED:
      return 'Off';
    default:
      return assertUnreachable(level);
  }
};

export const SeveritySelector = ({
  value,
  onSelect,
  disabled,
}: {
  value: GraphQLTypes.LintDiagnosticLevel;
  onSelect: (newSeverity: GraphQLTypes.LintDiagnosticLevel) => void;
  disabled?: boolean;
}) => {
  return (
    <Menu isLazy placement="bottom-end">
      <MenuButton
        as={Button}
        variant="hidden"
        isDisabled={disabled}
        leftIcon={<SeverityLevelIcon severity={value} />}
        rightIcon={!disabled ? <IconChevronDown /> : undefined}
      >
        {getSeverityLevelName(value)}
      </MenuButton>
      <SeverityList value={value} onSelect={onSelect} />
    </Menu>
  );
};

export const BulkSeveritySelector = ({
  disabled,
  value,
  onSelect,
}: {
  disabled: boolean;
  value: GraphQLTypes.LintDiagnosticLevel | undefined;
  onSelect: (newSeverity: GraphQLTypes.LintDiagnosticLevel) => void;
}) => (
  <Menu isLazy placement="bottom-end">
    <MenuButton
      as={Button}
      size="sm"
      variant="secondary"
      leftIcon={value ? <SeverityLevelIcon severity={value} /> : undefined}
      rightIcon={<IconChevronDown />}
      onClick={(e) => e.stopPropagation()}
      isDisabled={disabled}
    >
      {value === undefined ? 'Bulk update' : <>{getSeverityLevelName(value)}</>}
    </MenuButton>
    <SeverityList value={value} onSelect={onSelect} />
  </Menu>
);

const SeverityList = ({
  value,
  onSelect,
}: {
  value: GraphQLTypes.LintDiagnosticLevel | undefined;
  onSelect: (newSeverity: GraphQLTypes.LintDiagnosticLevel) => void;
}) => (
  <MenuContent>
    <MenuList onClick={(e) => e.stopPropagation()}>
      <MenuOptionGroup
        label="Linter Severity"
        type="radio"
        value={value}
        onChange={onSelect}
      >
        <MenuItemOption type="radio" value={LintDiagnosticLevel.ERROR}>
          <SeverityLevelIcon severity={LintDiagnosticLevel.ERROR} />{' '}
          <>{getSeverityLevelName(LintDiagnosticLevel.ERROR)}</>
        </MenuItemOption>
        <MenuItemOption type="radio" value={LintDiagnosticLevel.WARNING}>
          <SeverityLevelIcon severity={LintDiagnosticLevel.WARNING} />{' '}
          {getSeverityLevelName(LintDiagnosticLevel.WARNING)}
        </MenuItemOption>
        <MenuDivider />
        <MenuItemOption type="radio" value={LintDiagnosticLevel.IGNORED}>
          <SeverityLevelIcon severity={LintDiagnosticLevel.IGNORED} />{' '}
          {getSeverityLevelName(LintDiagnosticLevel.IGNORED)}
        </MenuItemOption>
      </MenuOptionGroup>
    </MenuList>
  </MenuContent>
);
